<template>
  <div class="flex align-items-center gap-0">
    <div v-if="shouldShowCursoSelector && cursoStore.hasMultiplosCursos" class="hidden md:flex align-items-center mr-1">
      <Select v-model="cursoStore.cursoAtual" :options="cursoStore.cursosDisponiveis" optionLabel="nome"
        placeholder="Selecione um curso" @change="onCursoChange" class="w-15rem md:w-20rem">
        <template #value="slotProps">
          <div v-if="slotProps.value" class="flex align-items-center">
            <i class="pi pi-graduation-cap text-primary mr-2" />
            <div class="white-space-nowrap overflow-hidden text-overflow-ellipsis">{{ slotProps.value.nome }}</div>
          </div>
          <span v-else>{{ slotProps.placeholder }}</span>
        </template>
        <template #option="slotProps">
          <div class="flex align-items-center">
            <i class="pi pi-graduation-cap text-primary mr-2" />
            <div>{{ slotProps.option.nome }}</div>
          </div>
        </template>
      </Select>
    </div>

    <Button :icon="themeToggleIcon" text rounded @click="toggleTheme" class="border-none text-secondary ml-1 mr-2"
      :aria-label="themeToggleLabel" />

    <div class="relative" v-show="authStore.isAuthenticated">
      <Button icon="pi pi-bars" text @click="toggleUserMenu" class="text-secondary border-none"
        aria-label="Abrir menu do usuário" />
      <Menu ref="userMenu" :model="userMenuItems" popup class="mt-1 w-18rem" />
    </div>

    <RoleChangeOverlay :visible="isChangingRole" :old-role-icon="oldRoleIcon" :new-role-icon="newRoleIcon"
      :message="roleChangeMessage" />
  </div>
</template>

<script lang="ts" setup>
import RoleChangeOverlay from "@/components/RoleChangeOverlay.vue";
import { useLogout } from "@/composables/useLogout";
import { useRoleSwitching } from "@/composables/useRoleSwitching";
import { useAuthStore } from "@/stores/AuthStore";
import { useCursoStore } from "@/stores/CursoStore";
import { useThemeStore } from "@/stores/ThemeStore";
import Button from "primevue/button";
import Menu from "primevue/menu";
import type { MenuItem } from "primevue/menuitem";
import Select from "primevue/select";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const themeStore = useThemeStore();
const authStore = useAuthStore();
const cursoStore = useCursoStore();
const userMenu = ref();
const { sair } = useLogout();
const {
  buildRoleMenuItems,
  isChangingRole,
  newRoleIcon,
  oldRoleIcon,
  roleChangeMessage,
} = useRoleSwitching();

const shouldShowCursoSelector = computed(() => {
  return authStore.isAuthenticated &&
    !authStore.isAdmin &&
    cursoStore.isReady;
});

const userMenuItems = computed(() => {
  const items: MenuItem[] = [
    {
      label: authStore.user?.name || "",
      class: "font-bold",
      disabled: true,
    },
    { separator: true },
  ];

  if (authStore.hasMultipleRoles) {
    items.push(
      {
        label: "Perfil atual: " + authStore.activeRoleLabel,
        icon: "pi pi-user",
        class: "font-semibold text-color",
        disabled: true,
      },
      ...buildRoleMenuItems({
        activeClass: "font-semibold surface-100 border-left-3 border-primary",
        inactiveLabelPrefix: "Mudar para ",
        showActiveRole: false,
        onBeforeSwitch: () => userMenu.value?.hide?.(),
      }),
      { separator: true },
    );
  }

  items.push(
    {
      label: "Minha Conta",
      icon: "pi pi-cog",
      command: () => {
        router.push({ name: "conta" });
      },
    },
    {
      label: "Central de Atendimento",
      icon: "pi pi-phone",
      command: () => {
        window.open("https://atendimento-dted.ufma.br/", "_blank");
      },
    },
    { separator: true },
    {
      label: "Sair",
      icon: "pi pi-sign-out",
      command: async () => {
        await sair();
      },
    },
  );

  return items;
});

function toggleUserMenu(event: Event) {
  userMenu.value?.toggle(event);
}

const themeToggleIcon = computed(() => themeStore.isDark ? "pi pi-sun" : "pi pi-moon");
const themeToggleLabel = computed(() => themeStore.isDark ? "Alternar para tema claro" : "Alternar para tema escuro");

const toggleTheme = () => {
  themeStore.toggleTheme();
};

const onCursoChange = () => {
  if (cursoStore.cursoAtual) {
    cursoStore.setCursoAtual(cursoStore.cursoAtual);
  }
};
</script>
